import { Vector3 } from 'three';

import {
	HIGHLIGHT_CIRCLE_PADDING,
	HIGHLIGHT_CIRCLE_Y_OFFSET,
	DEFAULT_CAMERA_Y,
	DEFAULT_CAMERA_XZ_DISTANCE,
	type SceneBoundsMetrics,
	type CameraPositionParams,
	type CameraGeometryResult,
	type BoundsVectors,
	type RawBounds
} from '../types/bounds-types';

/**
 * Calculate scene metrics from min/max bounds
 *
 * Consolidates duplicated calculations found throughout ThreeSceneEngine:
 * - Scene center (centerX, centerZ)
 * - Scene dimensions (width, depth, diagonal)
 * - Kitchen circle (radius, Y position)
 * - Highlight circle (radius, Y position)
 *
 * @param min - Minimum bounds vector
 * @param max - Maximum bounds vector
 * @returns Calculated scene metrics
 */
export const calculateSceneBoundsMetrics = (min: Vector3, max: Vector3): SceneBoundsMetrics => {
	// Calculate scene center
	const centerX = (min.x + max.x) / 2;
	const centerZ = (min.z + max.z) / 2;

	// Calculate scene dimensions
	const sceneWidth = max.x - min.x;
	const sceneDepth = max.z - min.z;
	const sceneDiagonal = Math.sqrt(sceneWidth * sceneWidth + sceneDepth * sceneDepth);

	// Kitchen circle: around the scene at mid height
	const kitchenCircleRadius = sceneDiagonal / 2;
	const kitchenCircleY = (min.y + max.y) / 2;

	// Highlight circle: larger and higher (for spotlight positioning)
	const highlightCircleRadius = kitchenCircleRadius + HIGHLIGHT_CIRCLE_PADDING;
	const highlightCircleY = max.y + HIGHLIGHT_CIRCLE_Y_OFFSET;

	return {
		centerX,
		centerZ,
		sceneWidth,
		sceneDepth,
		sceneDiagonal,
		kitchenCircleRadius,
		kitchenCircleY,
		highlightCircleRadius,
		highlightCircleY
	};
};

/**
 * Calculate camera position and target from scene geometry
 *
 * @param params - Camera position parameters
 * @returns Camera position and target vectors
 */
export const calculateCameraGeometry = (params: CameraPositionParams): CameraGeometryResult => {
	const {
		centerX,
		centerZ,
		circleRadius,
		cameraY = DEFAULT_CAMERA_Y,
		distanceMultiplier = 2.5,
		viewingAngle = -Math.PI / 2, // Default: -Z direction (front of kitchen)
		xzDistance
	} = params;

	// Use explicit xzDistance if provided, otherwise fall back to circle-based calculation
	const cameraDistance = xzDistance ?? (circleRadius * 2 * distanceMultiplier);

	// Camera is on the opposite side of the viewing angle
	const cameraAngle = viewingAngle + Math.PI;

	// Camera position
	const position = new Vector3(
		centerX + Math.cos(cameraAngle) * cameraDistance,
		cameraY,
		centerZ + Math.sin(cameraAngle) * cameraDistance
	);

	// Camera target is the pivot point (center of bounds at floor level)
	const target = new Vector3(
		centerX,
		0, // Pivot at floor level for consistent orbiting
		centerZ
	);

	return { position, target };
};

/**
 * Convert raw bounds data to Vector3 objects
 *
 * @param bounds - Raw bounds with x, y, z properties
 * @returns Min and max as Vector3 objects
 */
export const boundsToVectors = (bounds: RawBounds): BoundsVectors => ({
	min: new Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
	max: new Vector3(bounds.max.x, bounds.max.y, bounds.max.z)
});

/**
 * Clone bounds vectors (for storing state)
 *
 * @param bounds - Bounds vectors to clone
 * @returns Cloned bounds vectors
 */
export const cloneBounds = (bounds: BoundsVectors): BoundsVectors => ({
	min: bounds.min.clone(),
	max: bounds.max.clone()
});
